import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PET } from "@/constants/testIds";

const COLORS = ["#E76F51", "#F4A261", "#E9C46A", "#2A9D8F", "#8AB17D", "#B57BA6"];

export default function PetDialog({ open, onOpenChange, initialPet, onSaved }) {
  const [name, setName] = useState("");
  const [species, setSpecies] = useState("dog");
  const [breed, setBreed] = useState("");
  const [birthday, setBirthday] = useState("");
  const [notes, setNotes] = useState("");
  const [color, setColor] = useState(COLORS[0]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initialPet?.name || "");
      setSpecies(initialPet?.species || "dog");
      setBreed(initialPet?.breed || "");
      setBirthday(initialPet?.birthday || "");
      setNotes(initialPet?.notes || "");
      setColor(initialPet?.color || COLORS[0]);
    }
  }, [open, initialPet]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { name, species, breed, birthday, notes, color };
      const { data } = initialPet
        ? await api.patch(`/pets/${initialPet.id}`, payload)
        : await api.post("/pets", payload);
      toast.success(initialPet ? "Pet updated" : `Welcome, ${data.name}!`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-3xl border-[#EAE2D6] bg-[#FDFBF7]">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-[#2D232E]">
            {initialPet ? "Edit pet" : "Add a new pet"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[#6A606C] mb-1.5 block">Name</Label>
            <Input
              data-testid={PET.nameInput}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Biscuit"
              className="rounded-xl h-11 bg-white border-[#EAE2D6] focus-visible:ring-2 focus-visible:ring-[#E76F51]/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#6A606C] mb-1.5 block">Species</Label>
              <Select value={species} onValueChange={setSpecies}>
                <SelectTrigger data-testid={PET.speciesSelect} className="rounded-xl h-11 bg-white border-[#EAE2D6]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-2xl bg-white border-[#EAE2D6]">
                  <SelectItem value="dog">Dog</SelectItem>
                  <SelectItem value="cat">Cat</SelectItem>
                  <SelectItem value="bird">Bird</SelectItem>
                  <SelectItem value="rabbit">Rabbit</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#6A606C] mb-1.5 block">Breed (optional)</Label>
              <Input
                data-testid={PET.breedInput}
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                placeholder="Golden Retriever"
                className="rounded-xl h-11 bg-white border-[#EAE2D6]"
              />
            </div>
          </div>

          <div>
            <Label className="text-[#6A606C] mb-1.5 block">Birthday</Label>
            <Input
              data-testid={PET.birthdayInput}
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              className="rounded-xl h-11 bg-white border-[#EAE2D6]"
            />
          </div>

          <div>
            <Label className="text-[#6A606C] mb-1.5 block">Notes</Label>
            <Textarea
              data-testid={PET.notesInput}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Loves belly rubs and stealing socks."
              className="rounded-xl bg-white border-[#EAE2D6]"
            />
          </div>

          <div>
            <Label className="text-[#6A606C] mb-1.5 block">Color tag</Label>
            <div className="flex items-center gap-2">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setColor(c)}
                  style={{ backgroundColor: c }}
                  className={`w-8 h-8 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-[#2D232E] scale-110" : "hover:scale-105"}`}
                  aria-label={c}
                />
              ))}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              data-testid={PET.saveBtn}
              type="submit"
              disabled={busy}
              className="rounded-full bg-[#E76F51] hover:bg-[#F4A261] text-white font-semibold px-6 shadow-[0_6px_20px_rgba(231,111,81,0.25)]"
            >
              {busy ? "Saving..." : initialPet ? "Save changes" : "Add pet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
